/**
 * EL PRECIO Y LA FORMA DE UN BLOQUE.
 *
 * En su propio fichero para romper un ciclo de importación: el copy español
 * importaba los otros dos idiomas, y los otros dos importaban el precio del
 * español. En ejecución eso deja `PRECIO` sin definir a mitad de la carga.
 *
 * Un ciclo entre módulos compila y falla al arrancar. Sacar lo compartido a un
 * tercero es la única forma de que no vuelva.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 06/10/2026 · EL DUEÑO REVIERTE D27: el lanzamiento (12 US$ / 42.000 COP) es
 * ahora un precio FIJO de por vida mientras la cuenta siga activa — no sube. Por
 * eso `renuevaAlNormal` pasa a `false` y el 119,99 US$ queda SOLO como ancla
 * tachada. No hay subida que anunciar, así que la frase de renovación no se
 * pinta. El resto de esta nota explica por qué, cuando SÍ había subida, esa
 * frase era obligatoria — y por qué el verificador la exige por las dos caras.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * DECIDIDO POR EL DUEÑO EL 01/09/2026 (D27, cerró `DECISIONES-PENDIENTES §A1`):
 *
 *     «se renueva por el valor normal»  ← revertido el 06/10/2026, ver arriba
 *
 * Era la opción más defendible de las dos, y la que más obliga: al haber una
 * SUBIDA en la renovación, el precio de renovación deja de ser letra pequeña y
 * pasa a ser información obligatoria ANTES de cobrar, en los tres mercados.
 *
 *   · EE. UU. — la Negative Option Rule de la FTC y ROSCA exigen que las
 *     condiciones se muestren claramente ANTES de tomar el dato de pago.
 *   · UE — la Directiva 2011/83 exige el precio total antes de que el pedido
 *     obligue, y la 98/6 regula cómo se anuncia un precio rebajado.
 *   · Brasil — el CDC art. 31 exige información clara sobre precio antes de la
 *     contratación.
 *
 * Por eso `renuevaAlNormal` no es un booleano decorativo: si vale `true`, la
 * frase de renovación con la cifra TIENE que estar visible en el bloque de
 * precio de los tres idiomas; si vale `false` (hoy), esa frase y la cifra normal
 * NO pueden aparecer. Lo comprueba `scripts/comprobar-copy.ts` por las dos caras.
 * ─────────────────────────────────────────────────────────────────────────
 */

export const PRECIO = {
  lanzamientoUsd: 12,
  normalUsd: 119.99,
  moneda: "US$",

  /**
   * ═══════════════════════════════════════════════════════════════════════
   * LO QUE LA TARJETA PAGA DE VERDAD · 18/09/2026
   * ═══════════════════════════════════════════════════════════════════════
   *
   * La cifra grande es 12 US$, que es la referencia internacional del
   * producto. Pero **el cobro se hace en pesos colombianos**, porque la cuenta
   * de la pasarela es colombiana y solo liquida COP. El porqué entero, con la
   * limitación de Rapyd y el cambio con el que se fijó, está en
   * `apps/api/src/precio.ts` (`EL_COBRO`).
   *
   * ⚠ ESTA CIFRA TIENE QUE SER LA MISMA QUE COBRA LA API. Si la página dice
   * 42.000 y la tarjeta paga otra cosa, es publicidad engañosa en los tres
   * mercados a la vez. Lo cruza `verificar:precio`, que compara este número
   * con `EL_COBRO.lanzamiento` y sale en rojo si no cuadran.
   *
   * ── Y LA NOTA DEBAJO DE LA CIFRA ES OBLIGATORIA MIENTRAS ESTO EXISTA ────
   *
   * La FTC, la Directiva 2011/83 y el CDC art. 31 —los tres que se citan arriba
   * por la renovación— exigen que el precio total se vea ANTES de tomar el dato
   * de pago. Enseñar 12 US$ y cobrar pesos sin decirlo lo incumple, y Google lo
   * marca además como precio engañoso. La nota es `precio.nota_moneda` en los
   * tres idiomas, y `verificar:precio` exige que exista.
   */
  lanzamientoCop: 42_000,

  /** Ancla tachada (119,99 × 3.500 ≈ 420.000). No se cobra nunca. */
  normalCop: 420_000,

  /** Cómo se escribe el peso en la página. Con el código, para que no se lea como dólar. */
  monedaCobro: "COP",

  /**
   * 06/10/2026 · El dueño fija el lanzamiento de por vida: quien entra se queda
   * a 12 US$ / 42.000 COP mientras tenga la cuenta activa, sin subidas. Antes
   * (D27, 01/09/2026) renovaba al precio normal; se revierte. En `false`, la
   * frase de renovación y la cifra normal NO pueden aparecer en el bloque de los
   * tres idiomas; lo exige `scripts/comprobar-copy.ts`.
   */
  renuevaAlNormal: false,
} as const;

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * LA CIFRA EN PESOS, ESCRITA PARA QUE NO SE PUEDA LEER MAL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `42000` en castellano se escribe `42.000` y en inglés `42,000`. Y esto no
 * es una preferencia tipográfica: **en inglés el punto es el separador decimal**,
 * así que `42.000 COP` lo lee un angloparlante como CUARENTA Y DOS pesos —un
 * céntimo de dólar— en vez de cuarenta y dos mil.
 *
 * O sea que la misma cadena, en la página en inglés, se equivoca por mil. En un
 * dato que es el precio total que va a pagar la tarjeta, y que está ahí
 * precisamente porque la FTC obliga a que se entienda antes de pagar.
 *
 * Por eso el separador sale del idioma de la página y no de una constante. Y el
 * código `COP` va siempre detrás: es lo que impide que `$ 42.000` se lea como
 * dólares, que es el otro error posible.
 *
 * Se pasa el idioma en vez de usar el del navegador a propósito: la página puede
 * estar en inglés en un navegador configurado en español, y lo que manda es lo
 * que la persona está leyendo.
 */
const COMO_SE_ESCRIBEN_LOS_MILES: Readonly<Record<string, string>> = {
  es: "es-CO",
  en: "en-US",
  pt: "pt-BR",
};

export function elCobroEnPesos(idioma: string): string {
  const local = COMO_SE_ESCRIBEN_LOS_MILES[idioma] ?? "es-CO";
  return `$ ${PRECIO.lanzamientoCop.toLocaleString(local)} ${PRECIO.monedaCobro}`;
}

/**
 * LAS FOTOS DE LA PÁGINA DE VENTA · 14/09/2026
 *
 * Encargo del dueño al pedir la página nueva: nada de pantallas de ejercicios
 * («aburren»), y sí «una persona usando el móvil, feliz», en cualquier momento
 * del día. Son de la modelo de siempre (D60), generadas para esta página, con
 * la pantalla del móvil de espaldas a la cámara en las seis.
 *
 * Viven en `activos/landing/fotos/` (en S3, lo mismo) y las sirve el back por
 * `/activos/`, sin pedir cuenta: quien las ve no ha entrado
 * (`apps/api/src/http/activos-publicos.ts`). Las rutas van aquí, con nombre, y
 * `verificar:activos` comprueba que cada una existe en el disco — un `<img>`
 * roto no da error en ningún sitio.
 *
 * 17/09/2026 · Hasta ese día vivían en `landing/public/fotos/` y viajaban
 * dentro de la imagen. El dueño: todo el material multimedia, en S3.
 */
/*
 * 15/09/2026 · TRES FOTOS NUEVAS Y UNA ESCENA CAMBIADA, por el repaso del dueño con capturas.
 *
 *   · `manana`: «la mujer parece estar dentro de la mesa» (la encimera le cortaba la cintura).
 *     Rehecha con la encimera DETRÁS de ella; y en vertical (4:5), que es como va ahora la portada.
 *   · `coche` → `reunion`: «en esa foto, el brazo es gigante». Dos intentos dentro de un coche
 *     salieron con la cara cambiada o el volante encima; la escena pasa a la recepción de una
 *     oficina, antes de la reunión. La foto del coche sale de la carpeta (sigue en git).
 *   · `aula` y `oficina`: rehechas de más (brazo y piernas largos); el dueño las vio y las dio por
 *     buenas: «las fotos adjuntas están bien».
 *
 * Las originales y las pruebas están en `_previsualizacion/landing-fotos-2/`, fuera del repo.
 */
export const FOTOS = {
  manana: "/activos/landing/fotos/manana.webp",
  autobus: "/activos/landing/fotos/autobus.webp",
  aula: "/activos/landing/fotos/aula.webp",
  oficina: "/activos/landing/fotos/oficina.webp",
  reunion: "/activos/landing/fotos/reunion.webp",
  sofa: "/activos/landing/fotos/sofa.webp",
} as const;

/** Una foto con su texto alternativo, escrito en el idioma de la página. */
export type Foto = { readonly src: string; readonly alt: string };

export type Punto = {
  readonly titulo: string;
  readonly texto: string;
  readonly foto?: Foto;
  /**
   * 15/09/2026 · El punto que se saca de la lista y se pinta aparte, en su propia caja. Va AQUÍ,
   * entre los puntos, y no en un campo nuevo, para que la frase siga pasando por los filtros de
   * copy, jerga y dolencias, que leen `puntos` y no leerían un campo que no conocen.
   *
   * Lo usan dos bloques (16/09; hasta entonces decía «hoy solo la garantía»): la garantía de los
   * catorce días en `precio`, y «Y para todos los que valoran su voz…» en `para-quien`.
   */
  readonly destacado?: boolean;
};

export type Bloque = {
  readonly id: string;
  readonly eyebrow?: string;
  readonly titulo: string;
  readonly cuerpo?: string;
  readonly foto?: Foto;
  readonly puntos?: readonly Punto[];
};
